import * as React from 'react';
import { Button, Form, Spinner, Stack } from 'react-bootstrap';
import { StyledModal } from '../modal/StyledModal';
import { apiFetch } from '../../lib/utils';

interface IProjectReportSnapshotBackfillModalProps {
  onDismiss(): void;
}

const defaultJobId = '3fa85f64-5717-4562-b3fc-2c963f66afa6';

const toPrettyString = async (response: Response) => {
  const text = await response.text();
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

export function ProjectReportSnapshotBackfillModal({ onDismiss }: IProjectReportSnapshotBackfillModalProps) {
  const [jobId, setJobId] = React.useState(defaultJobId);
  const [dryRun, setDryRun] = React.useState(true);
  const [rebuildExisting, setRebuildExisting] = React.useState(true);
  const [limit, setLimit] = React.useState(0);
  const [output, setOutput] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(
        '/ProjectReportSnapshotBackfill/run',
        'POST',
        JSON.stringify({ jobId, dryRun, rebuildExisting, limit }),
        { 'Content-Type': 'application/json' }
      );
      const text = await toPrettyString(response);
      setOutput(text);

      try {
        const parsed = JSON.parse(text);
        if (parsed?.jobId) setJobId(String(parsed.jobId));
      } catch {
        // Keep plain text response as-is.
      }
    } catch (error: any) {
      setOutput(error?.message ?? String(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const query = jobId ? `?jobId=${encodeURIComponent(jobId)}` : '';
      const response = await apiFetch(`/ProjectReportSnapshotBackfill/logs${query}`);
      setOutput(await toPrettyString(response));
    } catch (error: any) {
      setOutput(error?.message ?? String(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledModal
      title="Project backfill"
      onDismiss={onDismiss}
      className="full-width"
      body={
        <Stack gap={3}>
          <Form.Group>
            <Form.Label>jobId</Form.Label>
            <Form.Control value={jobId} readOnly />
          </Form.Group>

          <Form.Check label="dryRun" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
          <Form.Check label="rebuildExisting" checked={rebuildExisting} onChange={(e) => setRebuildExisting(e.target.checked)} />

          <Form.Group>
            <Form.Label>limit</Form.Label>
            <Form.Control
              type="number"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            />
          </Form.Group>

          <Stack direction="horizontal" gap={2}>
            <Button className="button-primary" disabled={loading || !jobId} onClick={run}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Start'}
            </Button>
            <Button className="button-primary" disabled={loading || !jobId} onClick={fetchLogs}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Logs'}
            </Button>
          </Stack>

          <Form.Group>
            <Form.Label>Response</Form.Label>
            <Form.Control as="textarea" rows={12} readOnly value={output} />
          </Form.Group>
        </Stack>
      }
    />
  );
}
